/**
 * The business's theme presets (P5-014, contracts/theme-presets-v1.json).
 *
 * A preset changes only the accent family: primary controls, links,
 * selection and the focus ring. Surfaces, text, borders and every status
 * colour (information, success, warning and danger) stay the same in every
 * preset (Musweu, 16 October 2026), so a business's choice can never change
 * what a message means. Each preset is tested against the publication rules
 * in shipped-palette.test.ts, light and dark.
 */

import type { ThemeColorToken } from "./validate";

export const BRAND_TOKENS = [
  "accent",
  "accent-hover",
  "accent-soft",
  "accent-strong",
  "on-accent",
  "focus",
] as const satisfies readonly ThemeColorToken[];

export type BrandToken = (typeof BRAND_TOKENS)[number];
export type BrandPalette = Readonly<Record<BrandToken, string>>;

export const PRESET_KEYS = ["purple", "blue", "teal", "charcoal"] as const;
export type PresetKey = (typeof PRESET_KEYS)[number];
export const DEFAULT_PRESET: PresetKey = "purple";

export const PRESETS: Readonly<Record<PresetKey, { label: string; light: BrandPalette; dark: BrandPalette }>> = {
  // The palette index.css ships, unchanged.
  purple: {
    label: "Purple",
    light: {
      accent: "#443199",
      "accent-hover": "#372780",
      "accent-soft": "#e9e2fa",
      "accent-strong": "#3a2a85",
      "on-accent": "#ffffff",
      focus: "#443199",
    },
    dark: {
      accent: "#8c7ae6",
      "accent-hover": "#9d8dea",
      "accent-soft": "#3b3366",
      "accent-strong": "#c2b8f3",
      "on-accent": "#1c1b1f",
      focus: "#9787e8",
    },
  },
  blue: {
    label: "Blue",
    light: {
      accent: "#1d5bb0",
      "accent-hover": "#174a90",
      "accent-soft": "#dfe9f8",
      "accent-strong": "#164580",
      "on-accent": "#ffffff",
      focus: "#1d5bb0",
    },
    dark: {
      accent: "#6fa3ee",
      "accent-hover": "#86b3f1",
      "accent-soft": "#1f3352",
      "accent-strong": "#b5d0f7",
      "on-accent": "#1c1b1f",
      focus: "#7aaaf0",
    },
  },
  teal: {
    label: "Teal",
    light: {
      accent: "#0f6b6b",
      "accent-hover": "#0b5757",
      "accent-soft": "#d7efee",
      "accent-strong": "#0b5252",
      "on-accent": "#ffffff",
      focus: "#0f6b6b",
    },
    dark: {
      accent: "#4fbdb5",
      "accent-hover": "#6ccac3",
      "accent-soft": "#1a3a39",
      "accent-strong": "#a6e3de",
      "on-accent": "#1c1b1f",
      focus: "#5cc4bc",
    },
  },
  charcoal: {
    label: "Charcoal",
    light: {
      accent: "#3a3a42",
      "accent-hover": "#2a2a30",
      "accent-soft": "#e6e4e1",
      "accent-strong": "#2f2f35",
      "on-accent": "#ffffff",
      focus: "#3a3a42",
    },
    dark: {
      accent: "#c9c6cf",
      "accent-hover": "#dad7df",
      "accent-soft": "#3a3940",
      "accent-strong": "#e4e2e8",
      "on-accent": "#1c1b1f",
      focus: "#cfccd5",
    },
  },
};

/** A key the server sent, or the default if this build does not know it. */
export function presetOf(key: string | null | undefined): PresetKey {
  return PRESET_KEYS.find((preset) => preset === key) ?? DEFAULT_PRESET;
}

/** The raised surface each appearance ships (index.css), behind a preset's sample. */
export const SAMPLE_SURFACE = { dark: "#26262b", light: "#ffffff" } as const;
